"use client"

import React, { useContext, useState } from "react"
import { styled, useTheme, alpha } from "@mui/material/styles"
import Box from "@mui/material/Box"
import MuiDrawer from "@mui/material/Drawer"
import MuiAppBar from "@mui/material/AppBar"
import Toolbar from "@mui/material/Toolbar"
import Typography from "@mui/material/Typography"
import IconButton from "@mui/material/IconButton"
import MenuIcon from "@mui/icons-material/Menu"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import CssBaseline from "@mui/material/CssBaseline"
import { AppContext } from "../contexts/AppContext"
import { useNavigate } from "react-router-dom"
import Sidebar from "./Sidebar/Sidebar"
import MailIcon from "@mui/icons-material/Mail"
import InboxIcon from "@mui/icons-material/MoveToInbox"
import Avatar from "@mui/material/Avatar"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import ListItemIcon from "@mui/material/ListItemIcon"
import SettingsIcon from "@mui/icons-material/Settings"
import LogoutIcon from "@mui/icons-material/Logout"
import { Home, Scanner, Store, CreditCard } from "@mui/icons-material"
import { Fab, Tooltip, Divider, useMediaQuery } from "@mui/material"
import QRScanner from "../components/QR/QRScqnner"
import DetailDialog from "../components/DetailDialog/DetailDialog"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"

const drawerWidth = 260

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  // Subtle shadow that looks more natural
  boxShadow: "0 2px 10px 0 rgba(0, 0, 0, 0.05)",
  borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  backgroundColor: theme.palette.background.paper,
})

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  // Subtle shadow that looks more natural
  boxShadow: "0 2px 10px 0 rgba(0, 0, 0, 0.05)",
  borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  backgroundColor: theme.palette.background.paper,
})

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}))

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.leavingScreen,
  }),
  boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 12px -5px rgba(33, 150, 243, 0.4)",
  backgroundImage: `linear-gradient(195deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  zIndex: theme.zIndex.drawer - 1,
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}))

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  cursor: "pointer",
  transition: "all 0.2s",
  background: alpha(theme.palette.common.white, 0.2),
  boxShadow: "0 2px 5px 0 rgba(0,0,0,0.16), 0 2px 10px 0 rgba(0,0,0,0.12)",
  "&:hover": {
    transform: "scale(1.1)",
    boxShadow: "0 4px 10px 0 rgba(0,0,0,0.2), 0 4px 20px 0 rgba(0,0,0,0.19)",
  },
}))

const StyledBrandLogo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(0, 2),
}))

const drawerItems = [
  {
    path: "/home",
    title: "Home",
    icon: <Home />,
  },
  {
    path: "/stores",
    title: "Stores",
    icon: <Store />,
  },
  /*{
    path: "/contacts",
    title: "Contacts",
    icon: <InboxIcon />,
    nested: [
      {
        path: "/clients",
        title: "Clients",
        icon: <MailIcon />,
      },
      {
        path: "/fournisseurs",
        title: "Fournisseurs",
        icon: <MailIcon />,
      },
    ],
  },
  {
    path: "/dashboard",
    title: "Dashboard",
    icon: <MailIcon />,
  },*/
]

const drawerItemsPartner = [
]



const MiniDrawer = ({ children }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const [openReader, setOpenReader] = useState(false)
  const { state, dispatch } = useContext(AppContext)
  const [open, setOpen] = React.useState(state.isAdmin && !isMobile)
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)
  const avatarOpen = Boolean(anchorEl)
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      const response = await axios.get(`/api/partner/session`, {
        headers: { Authorization: state.token ? `Bearer ${state.token}` : "" },
      })
      if (response.status !== 200) {
        handleLogout();
      }
      if (response.data && response.data.status === "error") {
        handleLogout();
      }
      if (!response.data.payload.isApproved) {
        handleLogout();
      }
      
    },
  })

  const handleDrawerOpen = () => setOpen(true)
  const handleDrawerClose = () => setOpen(false)

  const handleLogout = () => {
    dispatch({ type: "LOGOUT" })
    localStorage.removeItem("user")
    navigate("/login")
    handleAvatarClose()
  }

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleAvatarClose = () => {
    setAnchorEl(null)
  }

  const handleSettingsClick = () => {
    navigate("/account-settings")
    handleAvatarClose()
  }

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <CssBaseline />

      

      <AppBar position="fixed" open={open}>
        <Toolbar>
          {state.isAdmin && <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 2,
              ...(open && { display: "none" }),
              "&:hover": {
                background: alpha(theme.palette.common.white, 0.1),
              },
            }}
          >
            <MenuIcon />
          </IconButton>}

          <Typography
            variant="h5"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              letterSpacing: "0.5px",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CreditCard sx={{ mr: 1 }} /> CARDZ HUB
          </Typography>

          {state.user && (
            <Tooltip title="Account Menu">
              <IconButton
                onClick={handleAvatarClick}
                size="small"
                sx={{
                  ml: 2,
                  transition: "transform 0.2s",
                  "&:hover": { transform: "scale(1.1)" },
                }}
              >
                <StyledAvatar>{state.user.email.charAt(0).toUpperCase()}</StyledAvatar>
              </IconButton>
            </Tooltip>
          )}

          <Menu
            anchorEl={anchorEl}
            open={avatarOpen}
            onClose={handleAvatarClose}
            PaperProps={{
              elevation: 3,
              sx: {
                overflow: "visible",
                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                mt: 1.5,
                borderRadius: 2,
                minWidth: 220,
                "& .MuiAvatar-root": {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                User Profile
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your account
              </Typography>
            </Box>

            <Divider />

            <MenuItem disabled sx={{ opacity: 0.8, py: 1.5 }}>
              <ListItemIcon>
                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>{state.user.email.charAt(0).toUpperCase()}</Avatar>
              </ListItemIcon>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {state.user.email}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  User Account
                </Typography>
              </Box>
            </MenuItem>

            <Divider />

            <MenuItem onClick={handleSettingsClick} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <Typography variant="body2">Account Settings</Typography>
            </MenuItem>

            <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              <Typography variant="body2">Logout</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {state.isAdmin && <Drawer variant="permanent" open={state.isAdmin && open}>
        <DrawerHeader>
          <StyledBrandLogo>
            {open && (
              <>
                <CreditCard color="primary" />
                <Typography variant="h6" color="primary" fontWeight="bold">
                  CARDZ HUB
                </Typography>
              </>
            )}
          </StyledBrandLogo>
          <IconButton
            onClick={handleDrawerClose}
            sx={{
              color: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>

        <Divider sx={{ opacity: 0.2 }} />

        {state.isAdmin && <Sidebar open={open} setOpen={setOpen} drawerItems={state?.session.payload?.isAdmin?drawerItems:drawerItemsPartner} />}
      </Drawer>}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          backgroundColor: alpha(theme.palette.background.default, 0.98),
          overflow: "auto",
          height: "100vh",
          pt: 10,
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export default MiniDrawer

